/**
 * Bloc « Mes SKILLCARDS » du tableau de bord élève ET joueur (« Curieux ») :
 * les plus récentes (celles à travailler en tête) + lien vers la page
 * complète, et « Ajouter des SKILLCARDS », qui ouvre le sélecteur INTERNE
 * (`AddSkillcardsButton` → page de la carte dans l'app, jamais le site). Une
 * carte s'ajoute à la collection en jouant un exercice relié, en l'achetant
 * (migration 0309) ou quand un prof l'assigne.
 */

import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import {
  recentSkillcards,
  skillcardsToRework,
  useStudentSkillcards,
} from '@/lib/cards/student-skillcards';
import { AddSkillcardsButton } from './skillcard-browser';
import { SkillcardList } from './skillcard-row';

export const DASHBOARD_SKILLCARDS_COUNT = 3;

export function StudentSkillcardsSection({
  studentId,
  allHref = '/student/skillcards',
  empty = 'Tes SKILLCARDS apparaîtront ici dès que tu travailles un exercice relié à une carte, que tu en achètes une ou quand ton prof t’en ajoute une.',
}: {
  studentId: string;
  /** Page complète « Mes SKILLCARDS » de l'espace courant. */
  allHref?: '/student/skillcards' | '/player/skillcards';
  empty?: string;
}) {
  const cards = useStudentSkillcards(studentId);
  const list = cards.data ?? [];
  if (cards.isLoading || cards.error) return null;
  // HORA vivant (0315) : les cartes qui ont le plus perdu passent en tête,
  // pour les retravailler ; les récentes complètent sans doublon.
  const rework = skillcardsToRework(list, DASHBOARD_SKILLCARDS_COUNT);
  const reworkIds = new Set(rework.map((c) => c.id));
  const recent = recentSkillcards(
    list.filter((c) => !reworkIds.has(c.id)),
    Math.max(0, DASHBOARD_SKILLCARDS_COUNT - rework.length),
  );

  return (
    <section className="space-y-3" aria-labelledby="my-skillcards-title">
      <div className="flex items-center justify-between gap-2">
        <h2 id="my-skillcards-title" className="font-display text-ink-900 text-lg font-bold">
          Mes SKILLCARDS
        </h2>
        {list.length > 0 && (
          <Link
            to={allHref}
            className="text-primary-600 flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
          >
            Tout voir <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </div>
      {rework.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-red-700">
            À retravailler : {rework.length > 1 ? 'elles perdent' : 'elle perd'} de l’HORA
          </p>
          <SkillcardList cards={rework} showAssigner empty={null} />
        </div>
      )}
      {(recent.length > 0 || rework.length === 0) && (
        <SkillcardList cards={recent} showAssigner empty={empty} />
      )}
      <AddSkillcardsButton userId={studentId} />
    </section>
  );
}
